import { ReactElement, useState } from "react";
import {
    Bar,
    BarChart as RechartsBarChart,
    Brush,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from "recharts";
import { Stack } from "@mantine/core";
import { Units } from "@flexidao/dto";
import { flexidaoGrey, formatNumber } from "@flexidao/ui-lib";
import { CustomTick } from "./custom-tick";
import { CustomTooltip } from "./custom-tooltip";
import { CustomLegend } from "./custom-legend";

export type BarGroupChartElement<DatasetKey extends string> = {
    label: string;
    values: Record<DatasetKey, number | null>;
};

type BarGroupChartWithBrushProps<DatasetKey extends string> = {
    numDecimals: number;
    units: Units;
    height: number;
    datasets: Record<
        DatasetKey,
        {
            label: string;
            color: string;
        }
    >;
    data: Array<BarGroupChartElement<DatasetKey>>;
    maxDefaultItems: number;
};

export const BarGroupChartWithBrush = <DatasetKey extends string>({
    numDecimals,
    units,
    height,
    datasets,
    data,
    maxDefaultItems,
}: BarGroupChartWithBrushProps<DatasetKey>): ReactElement => {
    const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set(Object.keys(datasets)));

    const yAxisFormatter = (v: number): string =>
        formatNumber(v, {
            numDecimals: 0,
            units,
            useNbSp: true,
        });

    const toggleKeyVisibility = (key: string): void => {
        setVisibleKeys((prev) => {
            const next = new Set(prev);

            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }

            return next;
        });
    };

    return (
        <Stack>
            <ResponsiveContainer width="100%" height={height}>
                <RechartsBarChart data={data}>
                    {/* Axes */}
                    <XAxis
                        dataKey="label"
                        color={flexidaoGrey[7]}
                        fontSize="10px"
                        angle={-45}
                        textAnchor="end"
                        minTickGap={1}
                        interval={0}
                        tick={CustomTick}
                    />

                    <YAxis
                        tickFormatter={yAxisFormatter}
                        color={flexidaoGrey[7]}
                        fontSize="10px"
                        orientation="right"
                    />

                    {/* Grid */}
                    <CartesianGrid strokeDasharray="3 3" stroke={flexidaoGrey[2]} />

                    {/* Tooltip */}
                    <Tooltip
                        filterNull={false}
                        content={(args: TooltipProps<number, string>): ReactElement => (
                            <CustomTooltip
                                visibleKeys={visibleKeys}
                                numDecimals={numDecimals}
                                units={units}
                                {...args}
                            />
                        )}
                    />

                    {/* Brush */}
                    <Brush
                        dataKey="label"
                        height={40}
                        startIndex={0}
                        endIndex={Math.min(maxDefaultItems, data.length) - 1}
                    />

                    {/* Bars */}
                    {Object.entries(datasets).map(
                        // @ts-ignore --> TypeScript does not infer the value correctly
                        ([datasetKey, { color, label }]): ReactElement | null =>
                            visibleKeys.has(datasetKey) ? (
                                <Bar
                                    key={datasetKey}
                                    dataKey={`values.${datasetKey}`}
                                    name={label}
                                    fill={color}
                                />
                            ) : null,
                    )}
                </RechartsBarChart>
            </ResponsiveContainer>

            <CustomLegend
                datasets={datasets}
                visibleKeys={visibleKeys}
                toggleKeyVisibility={toggleKeyVisibility}
            />
        </Stack>
    );
};
